/**
 * Conversation Store
 * Manages conversation state, circuits, and quick replies
 */

import { create } from 'zustand';
import type {
  Circuit,
  QuickReply,
  ConversationPhase,
  ConversationContext,
} from '@/types';

interface ConversationStore extends ConversationContext {
  // Actions
  updateContext: (updates: Partial<ConversationContext>) => void;
  setLastAgentMessage: (message: string) => void;
  setAvailableCircuits: (circuits: Circuit[]) => void;
  setSelectedCircuit: (circuit: Circuit | null) => void;
  setQuickReplies: (replies: QuickReply[]) => void;
  setCurrentDestination: (destination: string | null) => void;
  setCurrentSubtheme: (subtheme: string | null) => void;
  setConversationPhase: (phase: ConversationPhase) => void;
  reset: () => void;
  // Internal state for forcing updates
  circuitsUpdateKey: number;
}

const initialState: ConversationContext & { circuitsUpdateKey: number } = {
  currentDestination: null,
  currentSubtheme: null,
  conversationPhase: 'greeting',
  lastAgentMessage: '',
  availableCircuits: [],
  selectedCircuit: null,
  quickReplies: [],
  circuitsUpdateKey: 0,
};

/**
 * Conversation store hook
 */
export const useConversationStore = create<ConversationStore>((set) => ({
  ...initialState,

  updateContext: (updates) =>
    set((state) => ({
      ...state,
      ...updates,
    })),

  setLastAgentMessage: (message) =>
    set({ lastAgentMessage: message }),

  setAvailableCircuits: (circuits) =>
    set((state) => ({
      availableCircuits: circuits,
      circuitsUpdateKey: state.circuitsUpdateKey + 1, // Force update by changing key
      selectedCircuit: null, // Reset selected circuit when new circuits arrive
    })),

  setSelectedCircuit: (circuit) =>
    set({ selectedCircuit: circuit }),

  setQuickReplies: (replies) =>
    set({ quickReplies: replies }),

  setCurrentDestination: (destination) =>
    set({ currentDestination: destination }),

  setCurrentSubtheme: (subtheme) =>
    set({ currentSubtheme: subtheme }),

  setConversationPhase: (phase) =>
    set({ conversationPhase: phase }),

  reset: () => set(initialState),
}));
